import { MedicalCheckupListChangeType } from '../models/enums/medicalCheckupListChangeType';
import { MedicalCheckupListChangedEvent } from '../models/medicalCheckups/medicalCheckupListChangedEvent';
import { MedicalCheckupShortDto } from '../models/medicalCheckups/medicalCheckupShortDto';

export interface ApplyListChangeOptions {
    page: number;
    rowsPerPage: number;
    matches: (checkup: MedicalCheckupShortDto) => boolean;
}

function sortByIdDesc(items: MedicalCheckupShortDto[]): MedicalCheckupShortDto[] {
    return [...items].sort((a, b) => b.id - a.id);
}

function trimToPage(items: MedicalCheckupShortDto[], rowsPerPage: number): MedicalCheckupShortDto[] {
    return items.slice(0, rowsPerPage);
}

function wasMatching(
    event: MedicalCheckupListChangedEvent,
    existingIndex: number,
    matches: (checkup: MedicalCheckupShortDto) => boolean
): boolean {
    if (existingIndex >= 0) {
        return true;
    }

    if (event.previousStatus != null) {
        return matches({ ...event.checkup, status: event.previousStatus });
    }

    return false;
}

export function applyListChange(
    items: MedicalCheckupShortDto[],
    count: number,
    event: MedicalCheckupListChangedEvent,
    options: ApplyListChangeOptions
): { items: MedicalCheckupShortDto[]; count: number } {
    const { page, rowsPerPage, matches } = options;
    const nowMatches = matches(event.checkup);
    const existingIndex = items.findIndex(i => i.id === event.checkup.id);
    const hadMatched = wasMatching(event, existingIndex, matches);

    if (event.changeType === MedicalCheckupListChangeType.Removed || (!nowMatches && hadMatched)) {
        if (existingIndex >= 0) {
            const nextItems = items.filter(i => i.id !== event.checkup.id);
            return { items: nextItems, count: Math.max(0, count - 1) };
        }
        if (hadMatched && !nowMatches) {
            return { items, count: Math.max(0, count - 1) };
        }
        return { items, count };
    }

    if (event.changeType === MedicalCheckupListChangeType.Created) {
        if (!nowMatches) {
            return { items, count };
        }
        if (page === 0) {
            const nextItems = trimToPage(
                sortByIdDesc([event.checkup, ...items.filter(i => i.id !== event.checkup.id)]),
                rowsPerPage
            );
            return { items: nextItems, count: count + 1 };
        }
        return { items, count: count + 1 };
    }

    // Updated
    if (nowMatches && existingIndex >= 0) {
        const nextItems = [...items];
        nextItems[existingIndex] = event.checkup;
        return { items: sortByIdDesc(nextItems), count };
    }

    if (nowMatches && existingIndex < 0) {
        if (page === 0) {
            const nextItems = trimToPage(
                sortByIdDesc([event.checkup, ...items]),
                rowsPerPage
            );
            return { items: nextItems, count: count + 1 };
        }
        return { items, count: count + 1 };
    }

    if (!nowMatches && existingIndex >= 0) {
        return {
            items: items.filter(i => i.id !== event.checkup.id),
            count: Math.max(0, count - 1),
        };
    }

    return { items, count };
}
